/**
 * VisualPanel — the right "Visuel" sidebar.
 *
 * Owns:
 * - Template picker (ATS / Moderne / Classique / Pro)
 * - Preview engine (React / Typst), page count cap, margin size
 * - Export menu (Graphique PDF / Premium Typst PDF / ATS DOCX / ATS PDF / JSON backup)
 *
 * This component is UI-only — all state and handlers are passed in as props
 * so that the parent (App.tsx) remains the single source of truth. The goal
 * of extracting it is to shrink App.tsx and make each pane testable in
 * isolation, not to introduce a new state layer.
 */

import { memo } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Download,
  FileJson,
  FileText,
  Layout,
  Loader2,
  Palette,
  Sparkles,
  Target,
} from 'lucide-react';
import type { CVData } from '../../types';
import type { getTType } from '../../contexts/I18nContext';
import type { LayoutTemplate, PreviewEngine } from '../../hooks/useEditorUi';
import type { FieldVisibility } from '../../hooks/useCvState';
import { exportPdfViaApi } from '../../lib/export-pdf-api';
import { toast } from '../../lib/toast';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '../ui/select';

export type MarginSize = 'none' | 'small' | 'medium' | 'large';
export type PrintMode = 'graphique' | 'ats';

export interface VisualPanelProps {
  t: getTType;

  // Layout / responsive flags
  isMobile: boolean;
  mobileTab: 'edition' | 'ats' | 'preview';
  mobilePreviewSubTab: 'apercu' | 'visuel';
  isVisuelSidebarOpen: boolean;
  setIsVisuelSidebarOpen: (next: (prev: boolean) => boolean) => void;

  // Preview config
  layoutTemplate: LayoutTemplate;
  setLayoutTemplate: (next: LayoutTemplate) => void;
  previewEngine: PreviewEngine;
  setPreviewEngine: (next: PreviewEngine) => void;
  maxPages: number | null;
  setMaxPages: (next: number | null) => void;
  marginSize: MarginSize;
  setMarginSize: (next: MarginSize) => void;

  // Export menu
  exportMenuValue: string;
  setExportMenuValue: (next: string) => void;
  isExportingPremium: boolean;
  setIsExportingPremium: (next: boolean) => void;

  // CV data + field visibility (used by the Premium export)
  data: CVData;
  fieldVisibility: FieldVisibility;

  // Handlers
  handlePrint: (mode?: PrintMode) => void;
  handleExportDOCX: () => Promise<void> | void;
  handleExportJSON: () => void;
  getPrintFilename: () => string;
}

export const VisualPanel = memo(function VisualPanel(props: VisualPanelProps) {
  const {
    t,
    isMobile,
    mobileTab,
    mobilePreviewSubTab,
    isVisuelSidebarOpen,
    setIsVisuelSidebarOpen,
    layoutTemplate,
    setLayoutTemplate,
    previewEngine,
    setPreviewEngine,
    maxPages,
    setMaxPages,
    marginSize,
    setMarginSize,
    exportMenuValue,
    setExportMenuValue,
    isExportingPremium,
    setIsExportingPremium,
    data,
    fieldVisibility,
    handlePrint,
    handleExportDOCX,
    handleExportJSON,
    getPrintFilename,
  } = props;

  const outerClassName = `flex-shrink-0 transition-all duration-300 ease-in-out ${
    isMobile
      ? `absolute left-0 right-0 bottom-0 mobile-panel-top ${
          mobileTab === 'preview' && mobilePreviewSubTab === 'visuel'
            ? 'z-10 flex flex-col'
            : 'z-0 opacity-0 pointer-events-none invisible'
        }`
      : isVisuelSidebarOpen
        ? 'w-0 md:w-[280px]'
        : 'w-0 md:w-0'
  }`;

  const innerClassName =
    `${isMobile ? 'flex-1 min-h-0' : 'h-full min-h-0'} ` +
    `border-l border-white/10 glass ${isMobile ? '' : 'overflow-y-auto'} no-print scrollbar-hide ` +
    `transition-[opacity,transform] duration-300 ease-in-out ` +
    `${isMobile ? 'mobile-scroll-panel' : ''} ` +
    `${
      isVisuelSidebarOpen ||
      (isMobile && mobileTab === 'preview' && mobilePreviewSubTab === 'visuel')
        ? 'opacity-100 translate-x-0'
        : 'opacity-0 translate-x-2 pointer-events-none'
    } ` +
    `${
      isVisuelSidebarOpen && !isMobile
        ? 'fixed inset-y-0 right-0 z-40 w-full max-w-[100vw] md:relative md:inset-auto md:max-w-none'
        : ''
    } ${isMobile ? 'relative' : ''}`;

  return (
    <div className={outerClassName}>
      <button
        onClick={() => setIsVisuelSidebarOpen((prev) => !prev)}
        className="absolute top-[50%] left-0 -translate-y-1/2 -translate-x-1/2 z-30 w-8 h-12 rounded-full bg-white/10 hover:bg-white/20 border border-white/10 text-white/70 hover:text-white hidden md:flex items-center justify-center shadow-lg shadow-black/30"
        title={isVisuelSidebarOpen ? 'Masquer Visuel' : 'Afficher Visuel'}
        aria-label={isVisuelSidebarOpen ? 'Masquer Visuel' : 'Afficher Visuel'}
      >
        {isVisuelSidebarOpen ? <ChevronLeft size={14} aria-hidden="true" /> : <ChevronRight size={14} aria-hidden="true" />}
      </button>

      <div className={innerClassName}>
        <div className={`p-4 md:p-6 space-y-6 ${isMobile ? 'pt-3 pb-4 mobile-scroll-content' : ''}`}>
          {!isMobile && (
            <div className="space-y-4 pt-2">
              <h1
                className="flex items-center justify-between gap-2 text-xl font-black tracking-tight text-white cursor-pointer hover:text-purple-300 transition-colors mb-2"
                onClick={() => setIsVisuelSidebarOpen((prev) => !prev)}
              >
                <span className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-purple-600/20 flex items-center justify-center text-purple-400">
                    <Palette size={18} />
                  </div>
                  Visuel
                </span>
                {isVisuelSidebarOpen ? <ChevronLeft size={16} aria-hidden="true" /> : <ChevronRight size={16} aria-hidden="true" />}
              </h1>
            </div>
          )}

          {/* Template — ATS | Moderne | Classique | Pro */}
          <div className="space-y-3 border-t border-white/10 pt-4">
            <h2 className="flex items-center gap-2 text-base font-semibold text-white">
              <Layout size={18} className="text-purple-400" />
              Template
            </h2>
            <div className="flex gap-1">
              <button
                type="button"
                onClick={() => setLayoutTemplate('ats')}
                className={`flex-1 py-1.5 text-sm font-bold rounded-md transition-all ${
                  layoutTemplate === 'ats'
                    ? 'bg-green-600 text-white'
                    : 'text-white/40 hover:text-white/60 bg-white/5'
                }`}
              >
                ATS
              </button>
              <button
                type="button"
                onClick={() => setLayoutTemplate('moderne')}
                className={`flex-1 py-1.5 text-sm font-bold rounded-md transition-all ${
                  layoutTemplate === 'moderne'
                    ? 'bg-blue-600 text-white'
                    : 'text-white/40 hover:text-white/60 bg-white/5'
                }`}
              >
                Moderne
              </button>
              <button
                type="button"
                onClick={() => setLayoutTemplate('classique')}
                className={`flex-1 py-1.5 text-sm font-bold rounded-md transition-all ${
                  layoutTemplate === 'classique'
                    ? 'bg-blue-600 text-white'
                    : 'text-white/40 hover:text-white/60 bg-white/5'
                }`}
              >
                Classique
              </button>
              <button
                type="button"
                onClick={() => setLayoutTemplate('pro')}
                className={`flex-1 py-1.5 text-sm font-bold rounded-md transition-all ${
                  layoutTemplate === 'pro'
                    ? 'bg-purple-600 text-white'
                    : 'text-white/40 hover:text-white/60 bg-white/5'
                }`}
              >
                Pro
              </button>
            </div>
          </div>

          {/* Aperçu : moteur, pages, marges */}
          <div className="space-y-3 border-t border-white/10 pt-4">
            <h2 className="flex items-center gap-2 text-base font-semibold text-white">
              <Sparkles size={18} className="text-purple-400" />
              Aperçu
            </h2>
            <div className="space-y-3">
              <div>
                <label className="text-sm text-white/50 uppercase font-bold block mb-1">
                  Moteur d&apos;aperçu
                </label>
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => setPreviewEngine('react')}
                    className={`flex-1 py-1.5 text-sm font-bold rounded-md transition-all ${
                      previewEngine === 'react'
                        ? 'bg-amber-600 text-white'
                        : 'text-white/40 hover:text-white/60 bg-white/5'
                    }`}
                  >
                    React
                  </button>
                  <button
                    type="button"
                    onClick={() => setPreviewEngine('typst')}
                    className={`flex-1 py-1.5 text-sm font-bold rounded-md transition-all ${
                      previewEngine === 'typst'
                        ? 'bg-amber-600 text-white'
                        : 'text-white/40 hover:text-white/60 bg-white/5'
                    }`}
                  >
                    Typst
                  </button>
                </div>
              </div>
              <div>
                <label className="text-sm text-white/50 uppercase font-bold block mb-1">
                  Pages à afficher
                </label>
                <select
                  value={maxPages || 'all'}
                  onChange={(e) =>
                    setMaxPages(e.target.value === 'all' ? null : parseInt(e.target.value))
                  }
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white"
                >
                  <option value="all">Toutes</option>
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3">3</option>
                  <option value="4">4</option>
                  <option value="5">5</option>
                </select>
              </div>
              <div>
                <label className="text-sm text-white/50 uppercase font-bold block mb-1">Marges</label>
                <select
                  value={marginSize}
                  onChange={(e) => setMarginSize(e.target.value as MarginSize)}
                  className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white"
                >
                  <option value="none">Aucune</option>
                  <option value="small">{t('visuel.small')}</option>
                  <option value="medium">Moyennes</option>
                  <option value="large">Grandes</option>
                </select>
              </div>
            </div>
          </div>

          {/* Export */}
          <div className="space-y-3 border-t border-white/10 pt-4">
            <h2 className="flex items-center gap-2 text-base font-semibold text-white">
              <Download size={18} className="text-purple-400" />
              Export
            </h2>
            <Select
              value={exportMenuValue}
              onValueChange={async (value) => {
                setExportMenuValue(value);
                setTimeout(() => setExportMenuValue(''), 100);
                if (value === 'graphique') handlePrint('graphique');
                if (value === 'ats-pdf') handlePrint('ats');
                if (value === 'ats-docx') await handleExportDOCX();
                if (value === 'json') handleExportJSON();
                if (value === 'export-premium') {
                  setIsExportingPremium(true);
                  const result = await exportPdfViaApi(
                    data,
                    layoutTemplate,
                    { fieldVisibility, marginSize, pageMode: true },
                    getPrintFilename() + '.pdf',
                  );
                  setIsExportingPremium(false);
                  if (!result.ok) {
                    if (result.status === 429) {
                      toast.error(
                        'Quota dépassé : 3 exports/jour, 1 toutes les 10 min. Réessayez plus tard.',
                      );
                    } else {
                      toast.error(
                        (result.error ?? 'Erreur export PDF') +
                          '\n\nVous pouvez utiliser « Imprimer (enregistrer en PDF) » comme solution de secours.',
                      );
                    }
                  }
                }
              }}
            >
              <SelectTrigger
                className="w-full bg-blue-600 hover:bg-blue-500 text-white border-none font-bold shadow-[0_0_20px_rgba(37,99,235,0.3)] h-10 text-sm"
                disabled={isExportingPremium}
              >
                {isExportingPremium ? (
                  <>
                    <Loader2 size={14} className="mr-2 animate-spin" />
                    Génération du PDF…
                  </>
                ) : (
                  <>
                    <Download size={14} className="mr-2" />
                    Exporter le CV
                  </>
                )}
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-white/10 text-white">
                <SelectItem
                  value="graphique"
                  className="text-sm font-bold py-2 focus:bg-blue-600 focus:text-white cursor-pointer"
                >
                  <div className="flex items-center gap-2">
                    <Sparkles size={12} className="text-purple-400" /> Graphique (PDF)
                  </div>
                </SelectItem>
                <SelectItem
                  value="export-premium"
                  className="text-sm font-bold py-2 focus:bg-blue-600 focus:text-white cursor-pointer"
                  disabled={isExportingPremium}
                >
                  <div className="flex items-center gap-2">
                    <FileText size={12} className="text-amber-400" /> Export premium (PDF Typst)
                  </div>
                </SelectItem>
                <SelectItem
                  value="ats-docx"
                  className="text-sm font-bold py-2 focus:bg-blue-600 focus:text-white cursor-pointer"
                >
                  <div className="flex items-center gap-2">
                    <Target size={12} className="text-green-400" /> ATS (DOCX) ⭐
                  </div>
                </SelectItem>
                <SelectItem
                  value="ats-pdf"
                  className="text-sm font-bold py-2 focus:bg-blue-600 focus:text-white cursor-pointer"
                >
                  <div className="flex items-center gap-2">
                    <Target size={12} className="text-green-500" /> ATS (PDF)
                  </div>
                </SelectItem>
                <SelectItem
                  value="json"
                  className="text-sm font-bold py-2 focus:bg-blue-600 focus:text-white cursor-pointer"
                >
                  <div className="flex items-center gap-2">
                    <FileJson size={12} className="text-blue-400" /> Backup (JSON)
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
            {(() => {
              const isIOS =
                typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent);
              const isAndroid =
                typeof navigator !== 'undefined' && /Android/.test(navigator.userAgent);
              const tipKey = isMobile
                ? isIOS
                  ? 'visuel.exportPdfMobileTipIos'
                  : isAndroid
                    ? 'visuel.exportPdfMobileTipAndroid'
                    : 'visuel.exportPdfMobileTip'
                : 'visuel.exportPdfDesktopTip';
              return (
                <p className="text-sm text-white/50 mt-2 leading-snug" role="note">
                  {t(tipKey)}
                </p>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
});
